import { Form, Select } from "antd";
import { useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import { useAuth } from "../config/AuthProvider";
import { useClientOrgs } from "../customHooks/useClientOrgs";
import { useContainerDeliveryMethods } from "../customHooks/useContainerDeliveryMethods";
import { useContainerLoadTypes } from "../customHooks/useContainerLoadTypes";
import { useCountries } from "../customHooks/useCountries";
import { useCurrencies } from "../customHooks/useCurrencies";
import { useForwarders } from "../customHooks/useForwarders";
import { usePaymentTerms } from "../customHooks/usePaymentTerms";
import { usePortCodes } from "../customHooks/usePortCodes";
import { useRoles } from "../customHooks/useRoles";
import { useSupplierOrgs } from "../customHooks/useSupplierOrgs";
import { useTimezones } from "../customHooks/useTimezones";

export const validateNonNegativeNumber = (allowDecimal = false) => (_, value) => {
  if (value === undefined || value === null) return Promise.resolve();

  const number = Number(value);

  if (isNaN(number) || number < 0) {
    return Promise.reject(new Error("Enter a number greater than or equal to 0"));
  }

  if (allowDecimal) {
    const decimalRegex = /^\d+(\.\d{1,2})?$/;
    if (!decimalRegex.test(number.toString())) {
      return Promise.reject(new Error("Up to 2 decimal places allowed"));
    }
  } else {
    if (!Number.isInteger(number)) {
      return Promise.reject(new Error("Number must be greater than or equal to 0"));
    }
  }

  return Promise.resolve();
};

const colourPairs = [
  { bg: "bg-[#FFD5D2]", text: "text-[#C9372C]" },
  { bg: "bg-[#EAE1AD]", text: "text-[#BD5B00]" },
  { bg: "bg-[#C0E4EF]", text: "text-[#227D9B]" },
  { bg: "bg-[#BFE9D8]", text: "text-[#1F845A]" },
  { bg: "bg-[#C1D8FC]", text: "text-[#1868DB]" },
  { bg: "bg-[#F4CFFF]", text: "text-[#964AC0]" },
  { bg: "bg-[#D1D4DC]", text: "text-[#6B6E76]" },
  { bg: "bg-[#FFCDA1]", text: "text-[#8C564B]" },
];

const vesselColourMap = new Map();

export const getVesselColourClasses = (name) => {
  if (!vesselColourMap.has(name)) {
    vesselColourMap.set(
      name,
      colourPairs[vesselColourMap.size % colourPairs.length],
    );
  }
  return vesselColourMap.get(name);
};

export const PhoneCodeSelect = ({
  selectClassNames,
  showSearch = true,
  optionLabelProp = "label",
  popupMatchSelectWidth = false,
  ...rest
}) => {
  const { data: countries } = useCountries();
  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      optionLabelProp={optionLabelProp}
      popupMatchSelectWidth={popupMatchSelectWidth}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().startsWith(input.toLowerCase())}
      {...rest}
    >
      {countries?.map((c) => (
        <Select.Option
          key={c.countryCd}
          value={c.phoneCd}
          label={`+${c.phoneCd}`}
          searchKey={c.name}
        >
          +{c.phoneCd} - {c.name}
        </Select.Option>
      ))}
    </Select>
  );
};

export const CountryCodeSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  ...rest
}) => {
  const { data: countries } = useCountries();
  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().startsWith(input.toLowerCase())}
      {...rest}
    >
      {countries?.map((c) => (
        <Select.Option
          key={c.countryCd}
          value={c.countryCd}
          searchKey={c.name}
        >
          {c.name}
        </Select.Option>
      ))}
    </Select>
  );
};

export const CurrencyCodeSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  ...rest
}) => {
  const { data: currencies } = useCurrencies();
  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
      {...rest}
    >
      {currencies?.map((c) => (
        <Select.Option
          key={c.code}
          value={c.code}
          searchKey={`${c.code} ${c.name}`}
        >
          {`${c.code} (${c.name})`}
        </Select.Option>
      ))}
    </Select>
  );
};

export const PortCodeSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  ...rest
}) => {
  const { data: portCodes } = usePortCodes();
  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().startsWith(input.toLowerCase())}
      {...rest}
    >
      {portCodes?.map((pc) => (
        <Select.Option
          key={pc.portCode}
          value={pc.portCode}
          searchKey={pc.portCode}
        >
          <div className="flex gap-2 items-center">
            <ReactCountryFlag countryCode={pc.portCode.slice(0, 2)} svg className="!w-6 !h-6" />
            {pc.portCode}
          </div>
        </Select.Option>
      ))}
    </Select>
  );
};

export const PaymentTermSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  ...rest
}) => {
  const { data: paymentTerms } = usePaymentTerms();
  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
      {...rest}
    >
      {paymentTerms?.map((pt) => (
        <Select.Option
          key={pt.id}
          value={pt.id}
          searchKey={pt.paymentTerms}
        >
          {pt.paymentTerms}
        </Select.Option>
      ))}
    </Select>
  );
};

export const TimezoneSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  optionLabelProp = "label",
  showExtra = false,
  form,
  name = "timezone",
  ...rest
}) => {
  const { data: timezones } = useTimezones();
  const selectedTimezone = Form.useWatch(name, form);
  const selectedTzObject = timezones?.find((t) => t.ianaTimezone === selectedTimezone);

  return (
    <>
      <Select
        className={selectClassNames}
        showSearch={showSearch}
        popupMatchSelectWidth={popupMatchSelectWidth}
        optionLabelProp={optionLabelProp}
        filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
        {...rest}
      >
        {timezones?.map((t) => (
          <Select.Option
            key={t.displayName}
            value={t.ianaTimezone}
            label={t.ianaTimezone}
            searchKey={`${t.displayName} ${t.ianaTimezone}`}
          >
            {t.displayName}
          </Select.Option>
        ))}
      </Select>
      {showExtra && selectedTzObject && (
        <div className="text-sm text-gray-500">
          {selectedTzObject?.windowsTimezone + " " + selectedTzObject?.displayName}
        </div>
      )}
    </>
  );
};

export const ContainerLoadTypeSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  autoSelectFirst = false,
  value,
  onChange,
  onContainerLoadTypeChange,
  ...rest
}) => {
  const { data: containerLoadTypes } = useContainerLoadTypes();

  useEffect(() => {
    if (!autoSelectFirst) return;
    if (!containerLoadTypes?.length) return;
    if (value != null) return;

    const first = containerLoadTypes[0];
    onChange?.(first.shortCode);
    onContainerLoadTypeChange?.(first);
  }, [autoSelectFirst, containerLoadTypes, value, onChange, onContainerLoadTypeChange]);

  const handleChange = (shortCode) => {
    onChange?.(shortCode);

    const selected = containerLoadTypes?.find(clt => clt.shortCode === shortCode);
    if (selected) {
      onContainerLoadTypeChange?.(selected);
    }
  };

  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      value={value}
      onChange={handleChange}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
      {...rest}
    >
      {containerLoadTypes?.map(clt => (
        <Select.Option key={clt.shortCode} value={clt.shortCode} searchKey={`${clt.shortCode} ${clt.description}`}>
          {clt.description}
        </Select.Option>
      ))}
    </Select>);
};

export const ForwarderSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  autoSelectFirst = false,
  value,
  onChange,
  onForwarderChange,
  ...rest
}) => {
  const { data: forwarders } = useForwarders();
  useEffect(() => {
    if (!autoSelectFirst) return;
    if (!forwarders?.length) return;
    if (value != null) return;
    const first = forwarders[0];
    onChange?.(first.id);
    onForwarderChange?.(first);
  }, [autoSelectFirst, forwarders, value, onChange, onForwarderChange]);
  const handleChange = (forwarderId) => {
    onChange?.(forwarderId);
    const selected = forwarders?.find(f => f.id === forwarderId);
    if (selected) {
      onForwarderChange?.(selected);
    }
  };
  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      value={value}
      onChange={handleChange}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
      {...rest}
    >
      {forwarders?.map(f => (
        <Select.Option key={f.id} value={f.id} searchKey={f.companyName}>
          {f.companyName}
        </Select.Option>
      ))}
    </Select>
  );
};

export const ClientOrgSelect = ({
  selectClassNames,
  showSearch = true,
  optionLabelProp = "label",
  popupMatchSelectWidth = false,
  disabled,
  ...rest
}) => {
  const { data: clients } = useClientOrgs();

  return (
    <Select
      disabled={disabled}
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      optionLabelProp={optionLabelProp}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
      {...rest}
    >
      {clients?.map((c) => (
        <Select.Option
          key={c.id}
          value={c.id}
          label={c.supplierShortCode}
          searchKey={`${c.supplierShortCode} ${c.name}`}
        >
          {c.supplierShortCode}
        </Select.Option>
      ))}
    </Select>
  );
};

export const SupplierOrgSelect = ({
  selectClassNames,
  showSearch = true,
  optionLabelProp = "label",
  popupMatchSelectWidth = false,
  disabled,
  form,
  ...rest
}) => {
  const { user } = useAuth();
  const { data: suppliers } = useSupplierOrgs();

  useEffect(() => {
    if (user?.isSupplier) {
      form.setFieldsValue({
        supplierID: user.supplierID,
      });
    }
  }, [user]);

  return (
    <Select
      disabled={user?.isSupplier || disabled}
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      optionLabelProp={optionLabelProp}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
      {...rest}
    >
      {suppliers?.map((s) => (
        <Select.Option
          key={s.id}
          value={s.id}
          label={s.name}
          searchKey={`${s.supplierShortCode} ${s.name}`}
        >
          {s.supplierShortCode}
        </Select.Option>
      ))}
    </Select>
  );
};

export const RoleSelect = ({
  selectClassNames,
  popupMatchSelectWidth = false,
  form,
  disabled,
  ...rest
}) => {
  const { user } = useAuth();
  const { data: roles } = useRoles();

  useEffect(() => {
    if (user?.isSupplier) {
      form.setFieldsValue({ role: roles.find(r => r.name === "Supplier")?.id });
    }
  }, [roles]);

  return (
    <Select
      disabled={user?.isSupplier || disabled}
      className={selectClassNames}
      popupMatchSelectWidth={popupMatchSelectWidth}
      {...rest}
    >
      {roles?.map((r) => (
        <Select.Option
          key={r.id}
          value={r.id}
        >
          {r.name}
        </Select.Option>
      ))}
    </Select>
  );
};

export const ContainerDeliveryMethodSelect = ({
  selectClassNames,
  showSearch = true,
  popupMatchSelectWidth = false,
  autoSelectFirst = false,
  value,
  onChange,
  ...rest
}) => {
  const { data: containerDeliveryMethods } = useContainerDeliveryMethods();

  useEffect(() => {
    if (!autoSelectFirst) return;
    if (!containerDeliveryMethods?.length) return;
    if (value != null) return;

    const first = containerDeliveryMethods[0];
    onChange?.(first.id);
  }, [autoSelectFirst, containerDeliveryMethods, value, onChange]);

  return (
    <Select
      className={selectClassNames}
      showSearch={showSearch}
      popupMatchSelectWidth={popupMatchSelectWidth}
      value={value}
      onChange={onChange}
      filterOption={(input, option) => option?.searchKey?.toLowerCase().includes(input.toLowerCase())}
      {...rest}
    >
      {containerDeliveryMethods?.map((cdm) => (
        <Select.Option
          key={cdm.id}
          value={cdm.id}
          searchKey={cdm.name}
        >
          {cdm.name}
        </Select.Option>
      ))}
    </Select>
  );
};

export const getRowClassName = (_, index) =>
  index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]";

export const getDetentionColorClasses = (daysToDetentionStart) => {
  if (daysToDetentionStart <= 0) return "#EF4444";
  if (daysToDetentionStart <= 3) return "#FF7F50";
  if (daysToDetentionStart <= 5) return "#FFA500";
  return "#22C55E";
};

export const percentFormatter = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 2,
});